/*
  VCC information-page behavior
  - Repairs native and Shopify-sanitized policy accordions without changing their text.
  - Powers the public measurement-guide tabs.
*/

(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const directChild = (root, selector) => (
    [...root.children].find((child) => child.matches(selector)) || null
  );

  const wrapDirectSequence = (policy, selectors, tagName, className) => {
    if (directChild(policy, `.${className}`)) return null;

    const children = [...policy.children];
    for (let index = 0; index <= children.length - selectors.length; index += 1) {
      const sequence = children.slice(index, index + selectors.length);
      if (!sequence.every((node, offset) => node.matches(selectors[offset]))) continue;

      const wrapper = document.createElement(tagName);
      wrapper.className = className;
      wrapper.dataset.vccGeneratedStructure = 'true';
      sequence[0].before(wrapper);
      wrapper.append(...sequence);
      return wrapper;
    }

    return null;
  };

  const reconstructSanitizedPolicyStructure = (policy) => {
    wrapDirectSequence(
      policy,
      [
        '.vcc-policy__eyebrow',
        '.vcc-policy__title',
        '.vcc-policy__lead',
        '.vcc-policy__proof-grid',
        '.vcc-policy__updated'
      ],
      'header',
      'vcc-policy__hero'
    );

    wrapDirectSequence(
      policy,
      ['.vcc-policy__welcome-copy', '.vcc-policy__snapshot'],
      'section',
      'vcc-policy__welcome'
    );

    wrapDirectSequence(
      policy,
      ['.vcc-resale-notice__identity-copy', '.vcc-resale-notice__facts'],
      'section',
      'vcc-resale-notice__identity'
    );

    wrapDirectSequence(
      policy,
      ['.vcc-resale-notice__unit-mark', '.vcc-resale-notice__unit-copy'],
      'section',
      'vcc-resale-notice__unit'
    );

    wrapDirectSequence(
      policy,
      [
        '.vcc-ship-spotlight__top',
        '.vcc-ship-callouts',
        '.vcc-ship-chart',
        '.vcc-ship-note'
      ],
      'section',
      'vcc-ship-spotlight'
    );

    wrapDirectSequence(
      policy,
      ['strong', 'p'],
      'section',
      'vcc-policy__cancel'
    );

    wrapDirectSequence(
      policy,
      ['h2', 'p', 'a.vcc-policy__button'],
      'section',
      'vcc-policy__contact'
    );
  };

  const contactPagePath = () => {
    const routeRoot = window.Shopify?.routes?.root;
    if (
      typeof routeRoot !== 'string'
      || !routeRoot.startsWith('/')
      || routeRoot.includes('?')
      || routeRoot.includes('#')
      || routeRoot.includes('\\')
      || /\/{2,}/.test(routeRoot)
    ) {
      return null;
    }

    const normalizedRoot = routeRoot.replace(/\/+$/, '');
    return `${normalizedRoot}/pages/contact`;
  };

  const normalizePolicyContactLinks = (root = document) => {
    const contactPath = contactPagePath();
    if (!contactPath) return;

    root.querySelectorAll([
      '.vcc-shipping-policy',
      '.vcc-privacy-policy',
      '.vcc-terms-policy',
      '.vcc-resale-notice'
    ].join(',')).forEach((policy) => {
      const contact = directChild(policy, '.vcc-policy__contact');
      const button = contact && directChild(contact, 'a.vcc-policy__button');
      if (button?.getAttribute('href') === 'mailto:info@vintageclubclothing.com') {
        button.setAttribute('href', contactPath);
      }
    });
  };

  const policyAccordionsStartClosed = () => (
    typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 620px)').matches
  );

  const defaultOpenForFlattenedItem = (policy, index, startClosed) => {
    if (startClosed) return false;
    if (policy.classList.contains('vcc-shipping-policy')) return false;
    if (policy.classList.contains('vcc-terms-policy')) return index < 2;
    if (policy.classList.contains('vcc-contact-information-policy')) return index < 2;
    if (
      policy.classList.contains('vcc-return-policy')
      || policy.classList.contains('vcc-privacy-policy')
      || policy.classList.contains('vcc-resale-notice')
    ) {
      return index === 0;
    }
    return false;
  };

  const prepareNativeDetails = (policy, startClosed) => {
    policy.querySelectorAll('details.vcc-policy__detail').forEach((details) => {
      if (details.dataset.vccAccordionReady === 'true') return;

      const summary = directChild(details, 'summary');
      const body = directChild(details, '.vcc-policy__detail-body');
      if (!summary || !body) return;

      if (startClosed || policy.classList.contains('vcc-shipping-policy')) {
        details.removeAttribute('open');
      }

      details.dataset.vccAccordionReady = 'true';
    });
  };

  const prepareCustomButtonDetails = (policy, startClosed) => {
    policy.querySelectorAll('.vcc-policy__detail:not(details)').forEach((item) => {
      if (item.dataset.vccAccordionReady === 'true') return;

      const trigger = directChild(item, 'button.vcc-policy__detail-trigger');
      const body = directChild(item, '.vcc-policy__detail-body');
      if (!trigger || !body) return;

      const bodyId = body.id || `vcc-policy-panel-${Math.random().toString(36).slice(2, 10)}`;
      const initiallyOpen = !startClosed
        && !policy.classList.contains('vcc-shipping-policy')
        && (
          trigger.getAttribute('aria-expanded') === 'true'
          || item.classList.contains('is-open')
          || item.dataset.vccOpen === 'true'
        );

      body.id = bodyId;
      trigger.type = 'button';
      trigger.setAttribute('aria-controls', bodyId);

      const setOpen = (open) => {
        item.classList.toggle('is-open', open);
        trigger.setAttribute('aria-expanded', String(open));
        body.hidden = !open;
      };

      trigger.addEventListener('click', () => {
        setOpen(trigger.getAttribute('aria-expanded') !== 'true');
      });

      item.dataset.vccAccordionReady = 'true';
      setOpen(initiallyOpen);
    });
  };

  const readFlattenedSequence = (children, startIndex) => {
    const number = children[startIndex];
    const title = children[startIndex + 1];
    if (
      !number?.matches('.vcc-policy__detail-number')
      || !title?.matches('.vcc-policy__detail-title')
    ) {
      return null;
    }

    let cursor = startIndex + 2;
    let toggle = null;
    if (children[cursor]?.matches('.vcc-policy__detail-toggle')) {
      toggle = children[cursor];
      cursor += 1;
    }

    const body = children[cursor];
    if (!body?.matches('.vcc-policy__detail-body')) return null;

    return {
      startIndex,
      endIndex: cursor,
      number,
      title,
      toggle,
      body
    };
  };

  const reconstructFlattenedContainer = (container, policy, ordinalState, startClosed) => {
    const children = [...container.children];
    const sequences = [];

    for (let index = 0; index < children.length;) {
      const sequence = readFlattenedSequence(children, index);
      if (!sequence) {
        index += 1;
        continue;
      }
      sequences.push(sequence);
      index = sequence.endIndex + 1;
    }

    if (!sequences.length) return;

    let sequenceIndex = 0;
    while (sequenceIndex < sequences.length) {
      const run = [sequences[sequenceIndex]];
      while (
        sequenceIndex + 1 < sequences.length
        && sequences[sequenceIndex + 1].startIndex === run[run.length - 1].endIndex + 1
      ) {
        sequenceIndex += 1;
        run.push(sequences[sequenceIndex]);
      }

      const wrapper = container === policy
        ? document.createElement('section')
        : container;

      if (wrapper !== container) {
        wrapper.className = 'vcc-policy__details';
        wrapper.dataset.vccGeneratedDetails = 'true';
        run[0].number.before(wrapper);
      }

      run.forEach((sequence) => {
        const details = document.createElement('details');
        const summary = document.createElement('summary');
        const toggle = sequence.toggle || document.createElement('span');

        details.className = 'vcc-policy__detail';
        details.dataset.vccAccordionReady = 'true';
        toggle.classList.add('vcc-policy__detail-toggle');
        toggle.setAttribute('aria-hidden', 'true');
        sequence.number.setAttribute('aria-hidden', 'true');

        summary.append(sequence.number, sequence.title, toggle);
        details.append(summary, sequence.body);

        if (defaultOpenForFlattenedItem(policy, ordinalState.value, startClosed)) {
          details.setAttribute('open', '');
        }

        ordinalState.value += 1;
        if (wrapper === container) {
          sequence.number.before(details);
        } else {
          wrapper.append(details);
        }
      });

      sequenceIndex += 1;
    }
  };

  const reconstructFlattenedAccordions = (policy, startClosed) => {
    const ordinalState = { value: 0 };
    reconstructFlattenedContainer(policy, policy, ordinalState, startClosed);
    policy.querySelectorAll(':scope > .vcc-policy__details').forEach((list) => {
      reconstructFlattenedContainer(list, policy, ordinalState, startClosed);
    });
  };

  const normalizePolicyAccordions = (root = document) => {
    const startClosed = policyAccordionsStartClosed();

    root.querySelectorAll('.vcc-policy').forEach((policy) => {
      reconstructSanitizedPolicyStructure(policy);
      reconstructFlattenedAccordions(policy, startClosed);
      prepareNativeDetails(policy, startClosed);
      prepareCustomButtonDetails(policy, startClosed);
    });
  };

  const hydrateMeasurementImage = (image) => {
    if (!image || image.hasAttribute('src') || !image.dataset.src) return;
    image.src = image.dataset.src;
    image.removeAttribute('data-src');
  };

  const initMeasurementGuide = (root = document) => {
    root.querySelectorAll('[data-vcc-measure-guide]').forEach((guide) => {
      if (guide.dataset.vccMeasureReady === 'true') return;

      const tabs = [...guide.querySelectorAll('[role="tab"][data-vcc-measure-tab]')];
      const panels = [...guide.querySelectorAll('[role="tabpanel"][data-vcc-measure-panel]')];
      if (!tabs.length || !panels.length) return;

      const activate = (tab, focus = false) => {
        const target = tab.dataset.vccMeasureTab;
        tabs.forEach((candidate) => {
          const selected = candidate === tab;
          candidate.setAttribute('aria-selected', String(selected));
          candidate.tabIndex = selected ? 0 : -1;
          if (selected && focus) candidate.focus();
        });
        panels.forEach((panel) => {
          const selected = panel.dataset.vccMeasurePanel === target;
          if (selected) {
            hydrateMeasurementImage(panel.querySelector('[data-vcc-measure-image]'));
          }
          panel.hidden = !selected;
        });
      };

      tabs.forEach((tab, tabIndex) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            nextIndex = (tabIndex + 1) % tabs.length;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            nextIndex = (tabIndex - 1 + tabs.length) % tabs.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = tabs.length - 1;
          }
          if (nextIndex === null) return;
          event.preventDefault();
          activate(tabs[nextIndex], true);
        });
      });

      guide.dataset.vccMeasureReady = 'true';
      activate(tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0]);
    });
  };

  const initialize = (root = document) => {
    normalizePolicyAccordions(root);
    normalizePolicyContactLinks(root);
    initMeasurementGuide(root);
  };

  ready(() => {
    initialize(document);
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('shopify:section:reorder', () => initialize(document));
    window.addEventListener('beforeprint', () => {
      document.querySelectorAll('[data-vcc-measure-image]').forEach(hydrateMeasurementImage);
    });
  });
})();
